import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Destructive confirm dialog. When `confirmName` is provided, the user must
 * type it exactly (case-insensitive) to enable the delete button.
 */
export default function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  impact,
  confirmName,
  confirmLabel = "Delete permanently",
  onConfirm,
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTyped("");
      setBusy(false);
    }
  }, [open]);

  const requireType = !!confirmName;
  const canDelete = !requireType || typed.trim().toLowerCase() === confirmName.trim().toLowerCase();

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> {title}
          </DialogTitle>
          <DialogDescription className="text-foreground/80 whitespace-pre-line pt-1">
            {impact}
          </DialogDescription>
        </DialogHeader>

        {requireType && (
          <div className="space-y-2">
            <Label htmlFor="confirm-type" className="text-sm">
              Type <span className="font-semibold text-foreground">{confirmName}</span> to confirm
            </Label>
            <Input
              id="confirm-type"
              data-testid="confirm-delete-name-input"
              className="h-11"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              placeholder={confirmName}
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="h-11"
            autoFocus
            onClick={() => onOpenChange(false)}
            data-testid="confirm-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="h-11"
            disabled={!canDelete || busy}
            onClick={run}
            data-testid="confirm-delete-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
